import { useEffect, useMemo, useRef, useState } from 'react';
import './image.css';
import { GlitchFX, prefersReducedMotion } from './glitch';

export function Image(props: {
  src: string;
  width?: number;
  height?: number;
  caption?: string;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // stable random seed per src so glitch timing varies across images
  const seed = useMemo(() => {
    let h = 0;
    for (let i = 0; i < props.src.length; i++) {
      h = (h * 31 + props.src.charCodeAt(i)) >>> 0;
    }
    return h;
  }, [props.src]);

  // tracked rather than read once, so toggling the OS setting takes effect
  // without a reload
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !img || !canvas) return;
    // no canvas, no rAF, no shake: the plain <img> is left alone
    if (reduced) return;

    let fx: GlitchFX;
    try {
      fx = new GlitchFX(canvas, img, seed);
    } catch {
      return; // no 2d context, keep the plain <img>
    }

    let raf = 0;
    let visible = false;
    let live = false;

    // driving state
    let hover = 0;
    let hoverTarget = 0;
    let burst = 0;
    let vel = 0;
    let nextBurst = performance.now() + 6000 + Math.random() * 14000;

    let lastY = window.scrollY;
    let lastT = performance.now();
    let lastFrame = 0;

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);

      const rect = wrap.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const mid = rect.top + rect.height / 2;
      const centred = Math.max(0, 1 - Math.abs((mid - vh / 2) / (vh / 2)));

      const speed = Math.min(1, vel / 1200);
      // scroll speed drives it, but only near the middle of the viewport
      const intensity = Math.min(1, speed * (0.35 + centred));
      const idle = intensity < 0.02 && hover < 0.004 && burst < 0.01;
      // idle frames only need to keep the static (and any gif) moving
      if (idle && now - lastFrame < 40) return;
      lastFrame = now;

      hover += (hoverTarget - hover) * (hoverTarget ? 0.22 : 0.14);
      if (hover < 0.002 && !hoverTarget) hover = 0;
      burst *= 0.955;
      if (burst < 0.01) burst = 0;
      vel *= 0.86;
      if (vel < 5) vel = 0;

      if (now > nextBurst) {
        burst = 0.75 + Math.random() * 0.25;
        nextBurst = now + 12000 + Math.random() * 25000;
      }

      if (rect.width && rect.height) fx.resize(rect.width, rect.height);

      const drawn = fx.render(now, { intensity, hover, burst });
      if (drawn !== live) {
        live = drawn;
        wrap.dataset.live = drawn ? '1' : '0';
      }

      const shake = (intensity * 0.6 + burst) * 2.5;
      wrap.style.setProperty(
        '--shakeX',
        (Math.sin(now / 37 + seed) * shake).toFixed(2) + 'px'
      );
      wrap.style.setProperty(
        '--shakeY',
        (Math.cos(now / 53 + seed) * shake * 0.5).toFixed(2) + 'px'
      );
    };

    const start = () => {
      if (raf) return;
      lastFrame = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting);
        if (visible) start();
        else stop();
      },
      { rootMargin: '80px' }
    );
    io.observe(wrap);

    const onScroll = () => {
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const dy = Math.abs(window.scrollY - lastY);
      vel = vel * 0.6 + (dy / dt) * 1000 * 0.4;
      lastY = window.scrollY;
      lastT = now;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const onEnter = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // pulled towards the middle: entering from an edge would otherwise put
      // the impact point (and every crack) on the border
      fx.shatter(
        x + (rect.width / 2 - x) * 0.35,
        y + (rect.height / 2 - y) * 0.35
      );
      hoverTarget = 1;
      burst = 1;
      if (visible) start();
    };
    const onLeave = () => {
      hoverTarget = 0;
      burst = Math.max(burst, 0.5);
    };
    wrap.addEventListener('pointerenter', onEnter);
    wrap.addEventListener('pointerleave', onLeave);

    const onLoad = () => start();
    if (!img.complete) img.addEventListener('load', onLoad);

    start();

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      wrap.removeEventListener('pointerenter', onEnter);
      wrap.removeEventListener('pointerleave', onLeave);
      img.removeEventListener('load', onLoad);
      fx.destroy();
      wrap.dataset.live = '0';
      wrap.style.removeProperty('--shakeX');
      wrap.style.removeProperty('--shakeY');
    };
  }, [seed, reduced]);

  const box = {
    aspectRatio:
      props.width && props.height
        ? `${props.width} / ${props.height}`
        : undefined,
    width: props.width ? props.width + 'px' : 'auto',
    maxWidth: '100%',
  };

  return (
    <>
      <div
        ref={wrapRef}
        className="img-wrap"
        data-live="0"
        style={box}
      >
        <img
          ref={imgRef}
          className="img-base"
          src={props.src}
          alt={props.caption ?? ''}
          loading="lazy"
          style={{ width: '100%', height: props.height ? '100%' : 'auto' }}
        />
        <canvas ref={canvasRef} className="img-canvas" aria-hidden="true" />
      </div>
      {props.caption ? (
        <p className="w-auto text-left text-sm text-gray-500">
          {props.caption ?? props.src}
        </p>
      ) : null}
    </>
  );
}

export default Image;
